import { IA_MODEL } from '@educabot/educablocks-boards';
import Tutorial1Step1 from '@sections/creabots/components/steps/Tutorial1Step1';
import AIVerifyLearningTutorial3Step20 from '@sections/creabots/components/steps/AIVerifyLearningTutorial3Step20';
import {
  BLOCK_CATEGORY_TYPE,
  BOARD_ID_TYPE,
  CODE_CHECK_TYPE,
  COMMON_CLASSES,
  AI_PROJECTS_TYPE,
  KIT_NAME,
  MODAL_TYPE,
  Project,
  TRAIN_STEPS,
} from '@sections/creabots/types/kits';
import * as routes from '@educabot/educablocks-cosmos';
import TutorialSlider from '@sections/creabots/components/steps/TutorialSlider';
import { getTutorialImagesInfo } from '@sections/creabots/features/utils/getTutorialImagesInfo';
import { getAIConfigForBlock, getAIElementForBlock } from '../config';

const intlPrefix = 'creabots.tutorials.ai.projects.automaticFeeder';
const imgBaseUrl = '/images/creabots/ai/projects/automaticFeeder';

export const automaticFeeder: Project = {
  id: 3,
  name: `${intlPrefix}.name`,
  description: `${intlPrefix}.description`,
  image: `${routes.bloquesUri}${imgBaseUrl}/project_image.png`,
  boardId: BOARD_ID_TYPE.AI,
  iaModelType: IA_MODEL.IMAGES,
  tutorial: [
    // Step 1.
    {
      step: {
        stepId: 0,
        modal: MODAL_TYPE.FULL,
        component: Tutorial1Step1,
        videoUrl: 'https://storage.googleapis.com/educabot-aec-cdn-1/labs/comedero-automatico.mp4',
      },
    },
    // Step 2.
    {
      step: {
        stepId: 1,
        modal: MODAL_TYPE.FULL,
        component: TutorialSlider,
        images: getTutorialImagesInfo({
          kitName: KIT_NAME.AI,
          projectName: AI_PROJECTS_TYPE.AUTOMATIC_FEEDER,
          totalSteps: 22,
        }),
      },
    },
    // Step 3.
    {
      step: {
        stepId: 2,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step3`,
        image: `${imgBaseUrl}/steps/step3.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => /gato|cat/i.test(obj.samples.class0?.name?.toLowerCase() || ''),
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#mlClass-0',
            xOffset: 20,
            yOffset: -15,
          },
        },
      ],
    },
    // Step 4.
    {
      step: {
        stepId: 3,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step4`,
        image: `${imgBaseUrl}/steps/step4.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => obj.samplesModalActive === 'class0',
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#ml-takeSample-0',
            xOffset: 20,
            yOffset: -15,
          },
        },
      ],
    },
    // Step 5.
    {
      step: {
        stepId: 4,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step5`,
        image: `${imgBaseUrl}/steps/step5.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => obj.samples.class0?.image?.length > 0,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#capture-btn',
            xOffset: 0,
            yOffset: 0,
          },
        },
      ],
    },
    // Step 6.
    {
      step: {
        stepId: 5,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step6`,
        image: `${imgBaseUrl}/steps/step6.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => obj.samples.class0?.image?.length > 1,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#capture-btn',
            xOffset: 0,
            yOffset: 0,
          },
        },
      ],
    },
    // Step 7
    {
      step: {
        stepId: 6,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step7`,
        image: `${imgBaseUrl}/steps/step7.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => obj.samples.class0?.image?.length > 2,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#capture-btn',
            xOffset: 0,
            yOffset: 0,
          },
        },
      ],
    },
    // Step 8.
    {
      step: {
        stepId: 7,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step8`,
        image: `${imgBaseUrl}/steps/step8.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => obj.samples.class0?.image?.length > 3,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#capture-btn',
            xOffset: 0,
            yOffset: 0,
          },
        },
      ],
    },
    // Step 9.
    {
      step: {
        stepId: 8,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step9`,
        image: `${imgBaseUrl}/steps/step9.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => !obj.samplesModalActive,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#load-samples-btn',
            xOffset: 50,
            yOffset: -15,
          },
        },
      ],
    },
    // Step 10.
    {
      step: {
        stepId: 9,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step10`,
        image: `${imgBaseUrl}/steps/step10.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => Object.keys(obj.samples).includes('class1'),
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#add-class-btn',
            xOffset: 130,
            yOffset: -15,
          },
        },
      ],
    },
    // Step 11.
    {
      step: {
        stepId: 10,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step11`,
        image: `${imgBaseUrl}/steps/step11.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) =>
          /no[ ]?gato|not [ |-]?cat|NÃO[ ]?gato/i.test(obj.samples.class1?.name?.toLowerCase() || ''),
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#mlClass-1',
            xOffset: 20,
            yOffset: -15,
          },
        },
      ],
    },
    // Step 12.
    {
      step: {
        stepId: 11,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step12`,
        image: `${imgBaseUrl}/steps/step12.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => obj.samplesModalActive === 'class1',
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#ml-takeSample-1',
            xOffset: 20,
            yOffset: -15,
          },
        },
      ],
    },
    // Step 13.
    {
      step: {
        stepId: 12,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step13`,
        image: `${imgBaseUrl}/steps/step13.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => obj.samples.class1?.image?.length > 0,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#capture-btn',
            xOffset: 0,
            yOffset: 0,
          },
        },
      ],
    },
    // Step 14.
    {
      step: {
        stepId: 13,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step14`,
        image: `${imgBaseUrl}/steps/step14.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => obj.samples.class1?.image?.length > 1,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#capture-btn',
            xOffset: 0,
            yOffset: 0,
          },
        },
      ],
    },
    // Step 15.
    {
      step: {
        stepId: 14,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step15`,
        image: `${imgBaseUrl}/steps/step15.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => obj.samples.class1?.image?.length > 2,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#capture-btn',
            xOffset: 0,
            yOffset: 0,
          },
        },
      ],
    },
    // Step 16.
    {
      step: {
        stepId: 15,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step16`,
        image: `${imgBaseUrl}/steps/step16.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => obj.samples.class1?.image?.length > 3,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#capture-btn',
            xOffset: 0,
            yOffset: 0,
          },
        },
      ],
    },
    // Step 17.
    {
      step: {
        stepId: 16,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step17`,
        image: `${imgBaseUrl}/steps/step17.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => !obj.samplesModalActive,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#load-samples-btn',
            xOffset: 50,
            yOffset: -15,
          },
        },
      ],
    },
    // Step 18.
    {
      step: {
        stepId: 17,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step18`,
        image: `${imgBaseUrl}/steps/step18.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => obj.trainingStep === TRAIN_STEPS.TRAIN,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#ml-train',
            xOffset: 15,
            yOffset: -20,
          },
        },
      ],
    },
    // Step 19.
    {
      step: {
        stepId: 18,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step19`,
        image: `${imgBaseUrl}/steps/step19.png`,
        iaStep: TRAIN_STEPS.TRAIN,
        completionCode: (obj) => obj.modelTrained === true,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#train-btn',
            xOffset: -3,
            yOffset: -12,
          },
        },
      ],
    },
    // Step 20.
    {
      step: {
        stepId: 19,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step20`,
        image: `${imgBaseUrl}/steps/step20.png`,
        iaStep: TRAIN_STEPS.TRAIN,
        completionCode: (obj) => obj.trainingStep === TRAIN_STEPS.CLASSIFY,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#ml-analyze',
            xOffset: 15,
            yOffset: -20,
          },
        },
      ],
    },
    {
      step: {
        stepId: 19,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step20`,
        image: `${imgBaseUrl}/steps/step20.png`,
        iaStep: TRAIN_STEPS.CLASSIFY,
        completionCode: (obj) => obj.isClassifying === true,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#analyze-btn',
            xOffset: 135,
            yOffset: 145,
          },
        },
      ],
    },
    // Step 21.
    {
      step: {
        stepId: 20,
        modal: MODAL_TYPE.DOCKED,
        iaStep: TRAIN_STEPS.CLASSIFY,
        component: AIVerifyLearningTutorial3Step20,
      },
    },
    // Step 22.
    {
      step: {
        stepId: 21,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step23`,
        image: `${imgBaseUrl}/steps/step23.png`,
        iaStep: TRAIN_STEPS.CLASSIFY,
        completionCode: (obj) => obj.active === false,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#toggleBlocks',
            xOffset: -15,
            yOffset: -15,
          },
        },
      ],
    },
    // Step 23.
    {
      step: {
        stepId: 23,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step24`,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.MOTORS, 1),
        image: `${imgBaseUrl}/steps/step24.png`,
        completionCode: 'voidsetup\\(\\)\\{servo_A0\\.attach\\(A0\\);servo_A0.write\\(90\\);',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.MOTORS, 1),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 50,
            yOffset: 10,
          },
        },
      ],
    },
    // Step 24.
    {
      step: {
        stepId: 23,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step24`,
        image: `${imgBaseUrl}/steps/step24.png`,
        completionCode: 'voidsetup\\(\\)\\{servo_A0\\.attach\\(A0\\);servo_A0.write\\(0\\);',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 250,
            yOffset: 25,
          },
        },
      ],
    },
    {
      step: {
        stepId: 24,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step25`,
        image: `${imgBaseUrl}/steps/step25.gif`,
        completionCodeCheck: CODE_CHECK_TYPE.BUILT,
      },
      hints: [
        {
          element: {
            image: `${routes.bloquesUri}/images/creabots/hint_big.png`,
            class: COMMON_CLASSES.BUILD_BLOCKLY_BTN_CLASS,
            xOffset: -26,
            yOffset: -35,
          },
        },
      ],
    },
    // Step 25.
    {
      step: {
        stepId: 25,
        modal: MODAL_TYPE.DOCKED,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.MOTORS, 1),
        description: `${intlPrefix}.step26`,
        image: `${imgBaseUrl}/steps/step26.png`,
        completionCode: 'if\\(command=="MLClass0"\\)\\{servo_A0.write\\(90\\);\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.MOTORS, 1),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 150,
            yOffset: 135,
          },
        },
      ],
    },
    {
      step: {
        stepId: 26,
        modal: MODAL_TYPE.DOCKED,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.OUTPUTS, 7),
        description: `${intlPrefix}.step27`,
        image: `${imgBaseUrl}/steps/step27.png`,
        completionCode:
          'if\\(command=="MLClass0"\\)\\{servo_A0\\.write\\(90\\);SEMAFORO_A3\\.clear\\(\\);.*SEMAFORO_A3\\.show\\(\\);delayMicroseconds\\(300\\);\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.OUTPUTS, 7),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 150,
            yOffset: 170,
          },
        },
      ],
    },
    // Step 25.
    {
      step: {
        stepId: 26,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step27`,
        image: `${imgBaseUrl}/steps/step27.png`,
        completionCode:
          'if\\(command=="MLClass0"\\)\\{servo_A0\\.write\\(90\\);SEMAFORO_A3\\.clear\\(\\);SEMAFORO_A3\\.setPixelColor\\(0,SEMAFORO_A3\\.Color\\((153,255,153|102,255,153|51,255,51|51,204,0|0,153,0|0,102,0|0,51,0)\\)\\);SEMAFORO_A3\\.setPixelColor\\(1,SEMAFORO_A3\\.Color\\((153,255,153|102,255,153|51,255,51|51,204,0|0,153,0|0,102,0|0,51,0)\\)\\);SEMAFORO_A3\\.setPixelColor\\(2,SEMAFORO_A3\\.Color\\((153,255,153|102,255,153|51,255,51|51,204,0|0,153,0|0,102,0|0,51,0)\\)\\);.*SEMAFORO_A3\\.show\\(\\);delayMicroseconds\\(300\\);\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 355,
            yOffset: 195,
          },
        },
      ],
    },
    {
      step: {
        stepId: 27,
        modal: MODAL_TYPE.DOCKED,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.MOTORS, 1),
        description: `${intlPrefix}.step28`,
        image: `${imgBaseUrl}/steps/step28.png`,
        completionCode: 'if\\(command=="MLClass1"\\)\\{servo_A0.write\\(90\\);\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.MOTORS, 1),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 150,
            yOffset: 270,
          },
        },
      ],
    },
    {
      step: {
        stepId: 27,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step28`,
        image: `${imgBaseUrl}/steps/step28.png`,
        completionCode: 'if\\(command=="MLClass1"\\)\\{servo_A0.write\\(0\\);\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 350,
            yOffset: 280,
          },
        },
      ],
    },
    {
      step: {
        stepId: 28,
        modal: MODAL_TYPE.DOCKED,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.OUTPUTS, 7),
        description: `${intlPrefix}.step29`,
        image: `${imgBaseUrl}/steps/step29.png`,
        completionCode:
          'if\\(command=="MLClass1"\\)\\{servo_A0\\.write\\(0\\);SEMAFORO_A3\\.clear\\(\\);.*SEMAFORO_A3\\.show\\(\\);delayMicroseconds\\(300\\);\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.OUTPUTS, 7),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 150,
            yOffset: 310,
          },
        },
      ],
    },
    {
      step: {
        stepId: 28,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step29`,
        image: `${imgBaseUrl}/steps/step29.png`,
        completionCode:
          'if\\(command=="MLClass1"\\)\\{servo_A0\\.write\\(0\\);SEMAFORO_A3\\.clear\\(\\);SEMAFORO_A3\\.setPixelColor\\(0,SEMAFORO_A3\\.Color\\((255,204,204|255,102,102|255,0,0|204,0,0|153,0,0|102,0,0|255,51,51)\\)\\);SEMAFORO_A3\\.setPixelColor\\(1,SEMAFORO_A3\\.Color\\((255,204,204|255,102,102|255,0,0|204,0,0|153,0,0|102,0,0|255,51,51)\\)\\);SEMAFORO_A3\\.setPixelColor\\(2,SEMAFORO_A3\\.Color\\((255,204,204|255,102,102|255,0,0|204,0,0|153,0,0|102,0,0|255,51,51)\\)\\);.*SEMAFORO_A3\\.show\\(\\);delayMicroseconds\\(300\\);\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 355,
            yOffset: 325,
          },
        },
      ],
    },
    {
      step: {
        stepId: 29,
        modal: MODAL_TYPE.DOCKED,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.MOTORS, 1),
        description: `${intlPrefix}.step30`,
        image: `${imgBaseUrl}/steps/step30.png`,
        completionCode: 'if\\(command=="MLC_Stop"\\)\\{servo_A0.write\\(90\\);\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.MOTORS, 1),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 150,
            yOffset: 400,
          },
        },
      ],
    },
    {
      step: {
        stepId: 29,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step30`,
        image: `${imgBaseUrl}/steps/step30.png`,
        completionCode: 'if\\(command=="MLC_Stop"\\)\\{servo_A0.write\\(0\\);\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 350,
            yOffset: 410,
          },
        },
      ],
    },
    // Step 26.
    {
      step: {
        stepId: 30,
        modal: MODAL_TYPE.DOCKED,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.OUTPUTS, 7),
        description: `${intlPrefix}.step31`,
        image: `${imgBaseUrl}/steps/step31.png`,
        completionCode:
          'if\\(command=="MLC_Stop"\\)\\{servo_A0\\.write\\(0\\);SEMAFORO_A3\\.clear\\(\\);.*SEMAFORO_A3\\.show\\(\\);delayMicroseconds\\(300\\);\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.OUTPUTS, 7),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 150,
            yOffset: 430,
          },
        },
      ],
    },
    {
      step: {
        stepId: 30,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step31`,
        image: `${imgBaseUrl}/steps/step31.png`,
        completionCode:
          'if\\(command=="MLC_Stop"\\)\\{servo_A0\\.write\\(0\\);SEMAFORO_A3\\.clear\\(\\);SEMAFORO_A3\\.setPixelColor\\(0,SEMAFORO_A3\\.Color\\((0,0,0|51,51,51|102,102,102|25,25,25|15,15,15|30,30,30)\\)\\);SEMAFORO_A3\\.setPixelColor\\(1,SEMAFORO_A3\\.Color\\((0,0,0|51,51,51|102,102,102|25,25,25|15,15,15|30,30,30)\\)\\);SEMAFORO_A3\\.setPixelColor\\(2,SEMAFORO_A3\\.Color\\((0,0,0|51,51,51|102,102,102|25,25,25|15,15,15|30,30,30)\\)\\);.*SEMAFORO_A3\\.show\\(\\);delayMicroseconds\\(300\\);\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 355,
            yOffset: 455,
          },
        },
      ],
    },
    // Step 30.
    {
      step: {
        stepId: 31,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step32`,
        image: `${imgBaseUrl}/steps/step32.gif`,
        completionCodeCheck: CODE_CHECK_TYPE.BUILT,
      },
      hints: [
        {
          element: {
            image: `${routes.bloquesUri}/images/creabots/hint_big.png`,
            class: COMMON_CLASSES.BUILD_BLOCKLY_BTN_CLASS,
            xOffset: -26,
            yOffset: -35,
          },
        },
      ],
    },
    // Step 31.
    {
      step: {
        stepId: 32,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step33`,
        image: `${imgBaseUrl}/steps/step33.png`,
        completionCode: (obj) => obj.active === true,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#toggleAI',
            xOffset: -15,
            yOffset: -15,
          },
        },
      ],
    },
    // Step 32.
    {
      step: {
        stepId: 33,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step34`,
        image: `${imgBaseUrl}/steps/step34.png`,
        iaStep: TRAIN_STEPS.CLASSIFY,
        completionCode: (obj) => obj.isClassifying === true,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#analyze-btn',
            xOffset: 135,
            yOffset: 145,
          },
        },
      ],
    },
    {
      step: {
        stepId: 33,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step34`,
        image: `${imgBaseUrl}/steps/step34.png`,
        iaStep: TRAIN_STEPS.CLASSIFY,
        completionCode: (obj) => obj.bluetooth.connected === true,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#MLConnectBluetooth',
            xOffset: 70,
            yOffset: -10,
          },
        },
      ],
    },
    {
      step: {
        stepId: 34,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step35`,
        image: `${imgBaseUrl}/project_image.png`,
        iaStep: TRAIN_STEPS.CLASSIFY,
      },
    },
  ],
};

/**
 * AI RULES:
 *
 * (obj) => obj.samples.class0?.image?.length > 0
 * (obj) => Object.values(obj.samples).some(sample => Array.isArray(sample.image) && sample.image?.length > 1)
 * (obj) => Object.values(obj.samples).every(sample => Array.isArray(sample.image) && sample.image?.length > 1)
 * (obj) => Object.values(obj.samples).some((s: any) => s.name && s.name.toLowerCase() === 'palma')
 * (obj) => obj.modelTrained === true
 */
